import { useEffect, useState, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import AuthShell from "@/components/AuthShell";
import { useAuth } from "@/context/AuthContext";

export default function VerifyEmail() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const { refresh } = useAuth();
  const [status, setStatus] = useState(token ? "verifying" : "error");
  const [error, setError] = useState(token ? "" : "This confirmation link is missing its token.");
  const ran = useRef(false);

  useEffect(() => {
    if (!token || ran.current) return;
    ran.current = true;
    (async () => {
      try {
        await api.post("/auth/verify-email", { token });
        setStatus("success");
        try { await refresh(); } catch (e) { if (process.env.NODE_ENV === "development") console.warn("Post-verify session refresh failed", e); }
      } catch (err) {
        setError(formatApiError(err.response?.data?.detail) || err.message);
        setStatus("error");
      }
    })();
  }, [token, refresh]);

  return (
    <AuthShell testid="verify-email-page" title="Confirm your email" subtitle="We're confirming your email address.">
      {status === "verifying" && (
        <div className="text-sm text-kid-navy bg-kid-skytint border border-kid-line rounded-xl px-4 py-4" data-testid="verify-verifying">
          Confirming your email…
        </div>
      )}
      {status === "success" && (
        <div className="space-y-5" data-testid="verify-success">
          <div className="text-sm text-kid-navy bg-kid-skytint border border-kid-line rounded-xl px-4 py-4">
            Your email is confirmed — thank you! Your account is all set.
          </div>
          <Link to="/app" className="kid-btn kid-btn-primary w-full h-12" data-testid="verify-continue-button">Go to my dashboard</Link>
        </div>
      )}
      {status === "error" && (
        <div className="space-y-5" data-testid="verify-error">
          <div className="text-sm text-kid-coral bg-red-50 border border-red-100 rounded-xl px-4 py-3">{error}</div>
          <p className="text-sm text-kid-muted">You can request a new confirmation link from your account after signing in.</p>
          <Link to="/login" className="kid-btn kid-btn-primary w-full h-12" data-testid="verify-signin-button">Back to sign in</Link>
        </div>
      )}
    </AuthShell>
  );
}
